import React, { forwardRef, useState } from "react";

import styled from "styled-components";

import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Text from "components/atoms/text/Text";

const Container = styled.div`
    margin: ${(props) => props.margin};
    width: ${(props) => props.width || "100%"};
`;

const LabelRow = styled.div`
    display: ${(props) => props.labelDisplay || "flex"};
    justify-content: space-between;
    align-items: center;
    margin: 0 0 4px 0;
`;

const LabelWrap = styled.div`
    display: flex;
    align-items: center;
`;

const RightInfoWrap = styled.div`
    display: flex;
    align-items: center;
`;

const TooltipButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 16px;
    height: 16px;
    border-radius: 8px;
    border: transparent;
    background-color: var(--color-SubBlack);
    cursor: pointer;
`;

const QuestionText = styled.p`
    position: relative;
    top: 6.1px;
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--color-White);
`;

const InputWrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 12px;
    width: 100%;
    height: ${(props) => props.height || "40"}px;
    background-color: ${(props) =>
        props.disabled ? "var(--color-DisabledInput)" : props.bgColor || "var(--color-White)"};
    border-radius: 4px;
    border: ${(props) => (props.borderless ? "none" : "solid 1px var(--color-InputLine)")};
    transition: all 80ms ease-in;

    &:focus-within {
        border: ${(props) => (props.borderless ? "none" : "solid 1px var(--color-Key)")};
    }
`;

const Input = styled.input`
    padding: 0;
    width: 100%;
    height: 24px;
    vertical-align: center;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-SubBlack);
    background-color: transparent;
    border-radius: 4px;
    border: 0;
    transition: all 120ms ease-in;
    text-align: ${(props) => props.textAlign || "left"};
    &:focus {
        outline: none;
    }

    &::placeholder {
        color: var(--color-Grey2);
    }

    &:-webkit-autofill {
        -webkit-box-shadow: 0 0 0 1000px var(--color-White) inset;
        box-shadow: 0 0 0 1000px var(--color-White) inset;
    }

    &:-webkit-autofill:active {
        -webkit-text-fill-color: var(--color-Black);
        text-fill-color: var(--color-Black);
    }
`;

const IconWrap = styled.div`
    position: relative;
    left: 7px;
    display: ${(props) => props.iconDisplay || "none"};
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
`;

const PasswordToggleIconWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
`;

const TimerViewWrap = styled.div`
    display: ${(props) => props.timerDisplay || "none"};
    margin: 0;
    width: 28px;
`;

const ErrorMessageWrap = styled.div`
    position: absolute;
    top: ${(props) => props.top || "44"}px;
    left: 0;
`;

const FormInput = forwardRef(
    (
        {
            margin,
            width,
            style,
            labelDisplay,
            labelDp,
            label,
            requiredMark,
            subMessage,
            subMessageDisplay,
            subMessageDP,
            rightInfoText,
            tooltipMessage,
            height,
            placeholder,
            type,
            maxLength,
            value,
            onChange,
            onKeyDown,
            disabled,
            iconDisplay,
            iconDp,
            timerDisplay,
            timerDp,
            children,
            errorMessage,
            onBlur,
            onFocus,
            name,
            textAlign,
            id,
            accept,
            bgColor,
            inputBgColor,
            borderless,
        },
        ref,
    ) => {
        const [showPassword, setShowPassword] = useState(false);
        const inputHeight = Number(height || 40);
        const errorTop = Number.isNaN(inputHeight) ? 44 : inputHeight + 4;

        const togglePasswordVisibility = () => {
            setShowPassword((prevState) => !prevState);
        };

        return (
            <Container margin={margin} width={width} style={style}>
                <LabelRow labelDisplay={labelDisplay || labelDp}>
                    <LabelWrap>
                        <Text variant="title-xs" color={"var(--color-SubBlack)"}>
                            {label}
                        </Text>
                        {requiredMark && (
                            <Text variant="title-lg" margin={"0 0 0 2px"} color={"var(--color-Key)"}>
                                *
                            </Text>
                        )}
                    </LabelWrap>
                    <RightInfoWrap>
                        {rightInfoText ? (
                            <Text variant="body-sm" margin={"0 4px 0 0"} color={"var(--color-SubBlack)"}>
                                {rightInfoText}
                            </Text>
                        ) : (
                            <Text
                                variant="body-sm"
                                color={"var(--color-Grey1)"}
                                display={subMessageDisplay || subMessageDP || "none"}
                            >
                                {subMessage}
                            </Text>
                        )}
                        {tooltipMessage && (
                            <Tooltip message={tooltipMessage}>
                                <TooltipButton>
                                    <QuestionText>?</QuestionText>
                                </TooltipButton>
                            </Tooltip>
                        )}
                    </RightInfoWrap>
                </LabelRow>
                <InputWrapper
                    height={height}
                    disabled={disabled}
                    bgColor={bgColor || inputBgColor}
                    borderless={borderless}
                >
                    <Input
                        ref={ref}
                        id={id}
                        placeholder={placeholder}
                        type={showPassword ? "text" : type}
                        maxLength={maxLength}
                        value={value || ""}
                        name={name}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        disabled={disabled}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        textAlign={textAlign}
                        autoComplete={type === "password" ? "off" : "on"}
                        accept={accept}
                    />
                    <IconWrap iconDisplay={iconDisplay || iconDp}>
                        <Button
                            onlyIcon
                            onClick={togglePasswordVisibility}
                            bgColor={"transparent"}
                            hoverBgColor={"var(--color-ButtonHover4)"}
                        >
                            <PasswordToggleIconWrap>
                                <Icon name={showPassword ? "pwShow" : "pwHide"} size={"16px"} />
                            </PasswordToggleIconWrap>
                        </Button>
                    </IconWrap>
                    <TimerViewWrap timerDisplay={timerDisplay || timerDp}>
                        <Text variant="body-sm" color={"var(--color-Key)"}>
                            {children}
                        </Text>
                    </TimerViewWrap>
                    <ErrorMessageWrap top={errorTop}>
                        <Text variant="body-sm" color={"var(--color-Warn)"}>
                            {errorMessage}
                        </Text>
                    </ErrorMessageWrap>
                </InputWrapper>
            </Container>
        );
    },
);

export default FormInput;
